import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { ITireGroove, defaultValue } from 'app/shared/model/tire-groove.model';
import { ITireLastOperation } from 'app/shared/model/tire-last-operation.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';
import axios from 'axios';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ITireGroove[],
  entity: defaultValue,
  lastOperation: null as ITireLastOperation | null,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

export type TireGrooveCustomState = Readonly<typeof initialState>;

const apiUrl = 'api/tire-grooves-custom';
const tireLastOperationApiUrl = 'api/tire-last-operations';

// Actions

export const getLatestTireGrooves = createAsyncThunk('getLatestTireGrooves/fetch_entity', async (tireIds: Array<number>) => {
  const requestUrl = `${apiUrl}/latest/${tireIds}`;
  return axios.get<ITireGroove[]>(requestUrl);
});

export const getTireLastOperation = createAsyncThunk('tireGrooveCustom/fetch_last_operation', async (truckImplementId: number) => {
  const requestUrl = `${tireLastOperationApiUrl}/truck-implement/${truckImplementId}`;
  return axios.get<ITireLastOperation>(requestUrl);
});

export const saveAllTireGrooves = createAsyncThunk(
  'tireGrooveCustom/create_entity',
  async (entities: ITireGroove[], thunkAPI) => {
    const result = await axios.post<ITireGroove[]>(`${apiUrl}/save-all`, entities.map(cleanEntity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const TireGrooveCustomSlice = createSlice({
  name: 'tireGrooveCustom',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getLatestTireGrooves), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(getTireLastOperation), (state, action) => {
        state.loading = false;
        state.lastOperation = action.payload.data;
      })
      .addMatcher(isRejected(getTireLastOperation), state => {
        state.loading = false;
        state.lastOperation = null;
      })
      .addMatcher(isFulfilled(saveAllTireGrooves), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entities = action.payload.data;
      })
      .addMatcher(isPending(getLatestTireGrooves), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(saveAllTireGrooves), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isRejected(saveAllTireGrooves), state => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = false;
      });
  },
});

export const { reset } = TireGrooveCustomSlice.actions;

// Reducer
export default TireGrooveCustomSlice.reducer;
